/**
 * Cookie/analytics consent banner for public visitor surfaces (backend#411).
 *
 * The Privacy Doctrine amendment allows anonymous per-visitor session
 * analytics on public clip pages, and requires consent to be collected
 * BEFORE any of it runs — GDPR/CCPA apply to visitors who never signed up.
 * This banner is that consent surface. The decision it stores is the gate
 * the future events SDK reads: see `src/lib/consent.ts` for the contract
 * (`localStorage["mc_visitor_analytics_consent"]`, absence = no tracking).
 *
 * Shown only on the surfaces an anonymous visitor hits where per-visitor
 * analytics would run — clip pages, the spotlight gallery, and bio pages —
 * never inside the authed owner surfaces. Non-blocking (the page stays
 * fully usable behind it), and Decline is a first-class button, not a
 * buried link. Both choices are sticky.
 *
 * The design mock carries no consent banner (checked 2026-08-20), so this
 * is deliberately minimal in the public pages' own design language.
 */

import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getVisitorAnalyticsConsent,
  isConsentSurface,
  setVisitorAnalyticsConsent,
  type VisitorAnalyticsConsent,
} from "@/lib/consent";

export default function ConsentBanner() {
  const location = useLocation();
  const [decision, setDecision] = useState<VisitorAnalyticsConsent | null>(() =>
    getVisitorAnalyticsConsent(),
  );

  if (decision !== null || !isConsentSurface(location.pathname)) return null;

  const decide = (value: VisitorAnalyticsConsent) => {
    setVisitorAnalyticsConsent(value);
    setDecision(value);
  };

  return (
    <div
      role="region"
      aria-label="Analytics consent"
      className="fixed inset-x-0 bottom-0 z-50 mx-auto mb-3 w-[min(36rem,calc(100%-1.5rem))] rounded-2xl border border-ios bg-ios-surface-raised/95 p-4 shadow-lg backdrop-blur"
    >
      <p className="text-[13px] leading-relaxed text-ios-secondary">
        This page can record anonymous, session-level analytics for its creator: a
        random session id, your country, device type and the site that referred you
        — never your IP address, browser details or identity. Data expires after 90
        days.{" "}
        <a href="/privacy" className="text-ios-accent-text hover:underline">
          Privacy policy
        </a>
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => decide("denied")}
          className="min-h-10 rounded-xl border border-ios bg-ios-surface-raised px-4 text-[13px] font-semibold text-ios transition-colors hover:bg-ios-surface-elevated"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={() => decide("granted")}
          className="min-h-10 rounded-xl bg-ios-accent px-4 text-[13px] font-semibold text-white transition-colors hover:bg-ios-accent/90"
        >
          Allow
        </button>
      </div>
    </div>
  );
}
